import * as React from "react"
import { cn } from "@/lib/utils"

interface AnimatedCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  gradient?: boolean
  hover3d?: boolean
  glow?: boolean
  spotlight?: boolean
}

const AnimatedCard = React.forwardRef<HTMLDivElement, AnimatedCardProps>(
  ({ className, children, gradient = false, hover3d = false, glow = false, spotlight = false, ...props }, ref) => {
    const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 })
    const cardRef = React.useRef<HTMLDivElement>(null)

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!hover3d || !cardRef.current) return
      
      const rect = cardRef.current.getBoundingClientRect()
      const x = (e.clientX - rect.left) / rect.width
      const y = (e.clientY - rect.top) / rect.height
      
      setMousePosition({ x, y })
    }

    const handleMouseLeave = () => {
      setMousePosition({ x: 0.5, y: 0.5 })
    }

    const transform = hover3d
      ? `perspective(1000px) rotateX(${(mousePosition.y - 0.5) * -10}deg) rotateY(${(mousePosition.x - 0.5) * 10}deg) translateZ(10px)`
      : undefined

    return (
      <div
        ref={ref}
        className={cn(
          "group relative rounded-2xl bg-white transition-all duration-300",
          gradient && "gradient-border",
          glow && "hover:shadow-glow-md",
          spotlight && "spotlight overflow-hidden",
          hover3d && "preserve-3d",
          className
        )}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          transform,
          transition: hover3d ? "transform 0.1s ease-out" : undefined,
        }}
        {...props}
      >
        <div ref={cardRef} className="relative z-10 h-full">
          {children}
        </div>
        {gradient && (
          <div className="absolute inset-0 rounded-2xl bg-gradient-premium opacity-0 transition-opacity duration-300 group-hover:opacity-5" />
        )}
      </div>
    )
  }
)
AnimatedCard.displayName = "AnimatedCard"

export { AnimatedCard }